import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { DialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums/analytic-events.enum';
import { getMappingTableColumnTypes } from '@app/modules/import/functions/get-mapping-table-column-types';
import { ColumnType } from '@app/modules/import/interfaces';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { StepChange } from '@import/enums/step-change.enum';
import { officeToOption } from '@import/functions/model-transformations/office-to-option.function';
import {
    EmployeeHumiColumnService,
    EmployeeRowAndColumnService,
    HumiColumnSmartMatchingService,
} from '@import/services';
import { Office } from '@models/company/office.model';

@Component({
    selector: 'app-form-employees-import-map-values',
    templateUrl: './map-value.template.html',
    styleUrls: ['./map-value.style.scss'],
})
/**
 * User value to enumerable column value mapping step
 *
 * Refactor notes: Offices pertain to the employee importer. When implementing a new importer, a generalized way of determining
 * what fields can be created should be introduced, likely through the service or parent view.
 */
export class MapValueForm extends BaseForm implements OnInit {
    @ViewChild('officeModal', { static: true }) officeModal: DialogComponent;
    @ViewChild('form', { static: true }) form: NgForm;
    @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
    createOfficePermission = Office.permission.edit;
    mappingComplete: boolean;
    private creationOptions: { key: string; humiColumn: ColumnType; formControl: FormControl<string | null> } | null;

    constructor(
        public importerService: EmployeeRowAndColumnService,
        public humiColumnService: EmployeeHumiColumnService,
        private smartMatchingService: HumiColumnSmartMatchingService,
        private auth: AuthService,
        private notify: NotifyService,
        private element: ElementRef,
        private analytics: AnalyticService
    ) {
        super();
    }

    checkErrors(): void {
        if (!this.form.valid) {
            this.notify.error('import.mapValueTemplate.assignError');
            this.element.nativeElement
                .querySelector('.empty')
                .scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        }
    }

    ngOnInit(): void {
        this.isLoading.emit(true);
        if (this.importerService.shouldSmartMatchValues()) {
            const humiColumns = getMappingTableColumnTypes(this.humiColumnService.employeeMappingTables);
            this.smartMatchingService.matchCsvColumnValues(humiColumns);
            this.submit();
        }
        this.updateMappingComplete();
        this.isLoading.emit(false);
    }

    updateMappingComplete(): void {
        // If one of the columns has more values than it has value mappings, then all the values have not been mapped.
        this.mappingComplete = this.humiColumnService
            .getValueMappableHumiColumns()
            .some((humiColumn: ColumnType) => humiColumn.csvColumn.values.size > humiColumn.csvColumn.valueMapping.size)
            ? undefined
            : true;
    }

    async createNewOffice(office: Office, form: BaseForm): Promise<void> {
        this.isLoading.emit(true);

        const { humiColumn, key } = this.creationOptions;
        try {
            await office.param('company', this.auth.company.id).save();
            humiColumn.options.push(officeToOption(office));
            humiColumn.csvColumn.valueMapping.set(key, office.id.toString());
            this.updateMappingComplete();

            // Cleanup
            this.creationOptions = null;
            form.reset();

            // Tracking
            this.importerService.setLastChange(StepChange.ValueMapping);
            this.analytics.trackEvent(AnalyticEvents.ImporterOfficeCreated);
        } catch (err) {
            this.notify.error(err || 'import.mapValueTemplate.unableToCreateOffice');
        }
        this.officeModal.open = false;
        this.isLoading.emit(false);
    }

    onCancel(form: BaseForm): void {
        this.officeModal.open = false;

        form.reset();

        if (!this.creationOptions) {
            return;
        }

        // Reset the select
        const { formControl, humiColumn } = this.creationOptions;
        const previousSelection = humiColumn.csvColumn.valueMapping.get(this.creationOptions.key) || null;
        formControl.setValue(previousSelection);
        this.creationOptions = null;
    }

    allowedToCreate(humiColumn: ColumnType): string[] {
        if (humiColumn.serializeName === 'officeId') {
            return this.createOfficePermission;
        }

        return [];
    }

    showModal(humiColumn: ColumnType): void {
        if (humiColumn.serializeName === 'officeId') {
            this.officeModal.open = true;
        }
    }

    updateValueMapping(
        change: MatSelectChange,
        humiColumn: ColumnType,
        key: string,
        formControl: FormControl<string | null>
    ): void {
        const value: string = change.value;

        if (value === 'new') {
            this.creationOptions = {
                key,
                humiColumn,
                formControl,
            };
            this.showModal(humiColumn);

            return;
        }

        humiColumn.csvColumn.valueMapping.set(key, value);
        this.setServiceStepChange();
        this.updateMappingComplete();
        this.analytics.trackEvent(AnalyticEvents.ImporterStep3ValueRemapped);
    }

    private setServiceStepChange(): void {
        this.importerService.setLastChange(StepChange.ValueMapping);
    }
}
